import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminEmailSettings, updateAdminEmailSettings, testEmailConnection } from '../utils/api';
import './AdminEmailSettings.css';

function AdminEmailSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testStatus, setTestStatus] = useState('');

  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_pass: '',
    smtp_from: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const data = await getAdminEmailSettings(token);
      setSettings({
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_secure: data.smtp_secure || false,
        smtp_user: data.smtp_user || '',
        smtp_pass: '', // Never populate password field
        smtp_from: data.smtp_from || ''
      });
      setLoading(false);
    } catch (err) {
      setError('Kunne ikke laste e-postinnstillinger');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await updateAdminEmailSettings(token, settings);
      setSuccess('E-postinnstillinger oppdatert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Kunne ikke oppdatere innstillinger');
    }
  };

  const handleTestEmail = async () => {
    setTestStatus('');
    setError('');

    try {
      const token = localStorage.getItem('token');
      const result = await testEmailConnection(token);

      if (result.success) {
        setTestStatus(`✓ ${result.message}`);
      } else {
        setTestStatus(`✗ ${result.message}`);
      }

      setTimeout(() => setTestStatus(''), 10000);
    } catch (err) {
      setTestStatus(`✗ Feil: ${err.message}`);
      setTimeout(() => setTestStatus(''), 10000);
    }
  };

  if (loading) {
    return <div className="admin-email-loading">Laster...</div>;
  }

  return (
    <div className="admin-email-container">
      <div className="admin-email-header">
        <h1>E-postserver innstillinger</h1>
        <button onClick={() => navigate('/admin')} className="back-button">
          ← Tilbake til admin
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {testStatus && (
        <div className={testStatus.startsWith('✓') ? 'test-success' : 'test-error'}>
          {testStatus}
        </div>
      )}

      <div className="admin-email-section">
        <div className="info-banner">
          <h3>ℹ️ Om SMTP-innstillinger</h3>
          <p>
            Konfigurer SMTP-serveren som brukes til å sende e-postvarsler til brukere.
            Dette er typisk e-postserveren fra din webhotellleverandør.
          </p>
          <p>
            <strong>Eksempel for webhotell:</strong>
          </p>
          <ul>
            <li>SMTP Server: mail.ditdomene.no</li>
            <li>Port: 587 (TLS) eller 465 (SSL)</li>
            <li>Brukernavn: dinbruker@ditdomene.no</li>
            <li>Passord: ditt e-postpassord</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>SMTP Server (Host) *</label>
            <input
              type="text"
              value={settings.smtp_host}
              onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
              placeholder="mail.ditdomene.no"
              required
            />
            <small>E-postserveren din (f.eks. mail.ditdomene.no)</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Port *</label>
              <input
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                min="1"
                max="65535"
                required
              />
              <small>Vanligvis 587 (TLS) eller 465 (SSL)</small>
            </div>

            <div className="form-group checkbox-group-admin">
              <label>
                <input
                  type="checkbox"
                  checked={settings.smtp_secure}
                  onChange={(e) => setSettings({ ...settings, smtp_secure: e.target.checked })}
                />
                Bruk SSL (port 465)
              </label>
              <small>Aktiver for SSL (port 465), deaktiver for TLS (port 587)</small>
            </div>
          </div>

          <div className="form-group">
            <label>SMTP Brukernavn *</label>
            <input
              type="email"
              value={settings.smtp_user}
              onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
              placeholder="dinbruker@ditdomene.no"
              required
            />
            <small>Din e-postadresse</small>
          </div>

          <div className="form-group">
            <label>SMTP Passord</label>
            <input
              type="password"
              value={settings.smtp_pass}
              onChange={(e) => setSettings({ ...settings, smtp_pass: e.target.value })}
              placeholder="La stå tom for å beholde eksisterende passord"
            />
            <small>La feltet være tomt hvis du ikke vil endre passordet</small>
          </div>

          <div className="form-group">
            <label>Avsender (From-adresse) *</label>
            <input
              type="text"
              value={settings.smtp_from}
              onChange={(e) => setSettings({ ...settings, smtp_from: e.target.value })}
              placeholder="Skiftrapport <noreply@ditdomene.no>"
              required
            />
            <small>Navnet og adressen som vises som avsender (f.eks. "Skiftrapport {'<'}noreply@ditdomene.no{'>'}")</small>
          </div>

          <div className="button-group">
            <button type="submit" className="btn-primary">
              💾 Lagre innstillinger
            </button>
            <button type="button" onClick={handleTestEmail} className="btn-test">
              📧 Send test e-post
            </button>
          </div>
        </form>

        <div className="help-section">
          <h3>🔧 Feilsøking</h3>
          <ul>
            <li><strong>Autentiseringsfeil:</strong> Sjekk at brukernavn og passord er riktig</li>
            <li><strong>Tilkoblingsfeil:</strong> Sjekk at serveren og porten er riktig</li>
            <li><strong>SSL/TLS-feil:</strong> Prøv å endre mellom SSL (port 465) og TLS (port 587)</li>
            <li><strong>Timeout:</strong> Kontroller at firewall tillater utgående trafikk på valgt port</li>
          </ul>
          <p>
            <strong>Test e-post:</strong> Send en test e-post til din egen e-postadresse (admin-brukeren)
            for å verifisere at innstillingene fungerer.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminEmailSettings;
